import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, Tags, Image as ImageIcon, Bell, 
  LogOut, Plus, Edit, Trash2, Save, X, Eye,
  TrendingUp, MousePointer, FileText, ChevronRight, HelpCircle,
  CheckCircle, AlertCircle, Sparkles, BarChart3, Settings, Layers
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Categorias padrão (fallback)
const defaultCategories = [
  { id: 'perfumaria', name: 'Perfumaria', slug: 'perfumaria' },
  { id: 'maquiagem', name: 'Maquiagem', slug: 'maquiagem' },
  { id: 'skincare', name: 'Skincare', slug: 'skincare' },
  { id: 'cabelos', name: 'Cabelos', slug: 'cabelos' },
  { id: 'corpo-banho', name: 'Corpo e Banho', slug: 'corpo-banho' },
  { id: 'presentes', name: 'Presentes', slug: 'presentes' }
];

const bannerPositions = [
  { id: 'home_hero', name: 'Home - Banner Principal', desc: 'Banner grande no topo da página inicial', size: '1920 x 600 px' },
  { id: 'home_middle', name: 'Home - Banner Meio', desc: 'Banners menores no meio da home', size: '600 x 400 px' },
  { id: 'catalog_featured', name: 'Catálogo - Destaque', desc: 'Banner de ofertas no topo do catálogo', size: '1200 x 300 px' },
  { id: 'catalog_side', name: 'Catálogo - Lateral', desc: 'Banners na lateral do catálogo', size: '300 x 600 px' }
];

const imageSpecs = {
  product: { width: 800, height: 800, format: 'JPG, PNG ou WebP', maxSize: '5MB', tip: 'Use fundo branco ou transparente para melhor resultado' },
  brand: { width: 400, height: 200, format: 'PNG (fundo transparente) ou SVG', maxSize: '2MB', tip: 'Logo horizontal funciona melhor' },
  banner: { format: 'JPG ou PNG', maxSize: '5MB', tip: 'Use imagens de alta qualidade' },
  popup: { width: 600, height: 400, format: 'JPG ou PNG', maxSize: '3MB', tip: 'A imagem aparece no topo do popup' },
  variation: { width: 400, height: 400, format: 'JPG, PNG ou WebP', maxSize: '2MB', tip: 'Imagem da variação do produto' }
};

// Componente de Dica
const Tip = ({ children }) => (
  <div className="flex items-start gap-2 p-3 bg-pink-500/10 border border-pink-500/30 rounded-lg text-sm text-pink-200 mb-4">
    <HelpCircle size={16} className="mt-0.5 flex-shrink-0 text-pink-400" />
    <span>{children}</span>
  </div>
);

// Componente de Status
const StatusBadge = ({ active }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
    active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
  }`}>
    {active ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
    {active ? 'Ativo' : 'Inativo'}
  </span>
);

// Componente de Especificação de Imagem
const ImageSpec = ({ type, position }) => {
  const spec = imageSpecs[type];
  const bannerSize = position ? bannerPositions.find(p => p.id === position)?.size : null;
  
  return (
    <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
      <div className="flex items-start gap-2">
        <AlertCircle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-amber-200 space-y-1">
          <p className="font-semibold text-amber-300">📐 Especificações da Imagem:</p>
          {bannerSize && <p>• Tamanho ideal: <span className="font-mono bg-slate-700/50 px-1.5 py-0.5 rounded">{bannerSize}</span></p>}
          {spec?.width && !bannerSize && <p>• Tamanho ideal: <span className="font-mono bg-slate-700/50 px-1.5 py-0.5 rounded">{spec.width} x {spec.height} px</span></p>}
          <p>• Formato: <span className="font-mono bg-slate-700/50 px-1.5 py-0.5 rounded">{spec?.format}</span></p>
          <p>• Máximo: <span className="font-mono bg-slate-700/50 px-1.5 py-0.5 rounded">{spec?.maxSize}</span></p>
          {spec?.tip && <p className="text-slate-400 italic">💡 {spec.tip}</p>}
        </div>
      </div>
    </div>
  );
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState(defaultCategories);
  const [banners, setBanners] = useState([]);
  const [popup, setPopup] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  
  // Form states
  const [showProductForm, setShowProductForm] = useState(false);
  const [showBrandForm, setShowBrandForm] = useState(false);
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [productForm, setProductForm] = useState({ 
    name: '', brand: '', category: '', description: '', images: [], 
    is_promo: false, size: '', variations: [] 
  });
  const [brandForm, setBrandForm] = useState({ name: '', logo: '', description: '', featured: false });
  const [bannerForm, setBannerForm] = useState({ title: '', image: '', link: '', position: 'home_hero', order: 0 });
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', order: 0 });
  const [popupForm, setPopupForm] = useState({ title: '', description: '', image: '', button_text: 'Ver Oferta', button_link: '' });
  const [settingsForm, setSettingsForm] = useState({ 
    site_email: 'contato@disneylandia.com',
    site_phone: '(94) 99166-6990',
    site_whatsapp: '5594991666990',
    site_address: 'Av. Weyne Cavalcante, 720 - Canaã dos Carajás, PA',
    site_instagram: '@disneylandiacosmeticoscanaa',
    hero_indicator1_title: 'Beleza',
    hero_indicator1_subtitle: 'Cosméticos Premium',
    hero_indicator2_title: '100+',
    hero_indicator2_subtitle: 'Produtos',
    hero_indicator3_title: '100%',
    hero_indicator3_subtitle: 'Originais',
    hero_indicator4_title: 'Presentes',
    hero_indicator4_subtitle: 'Para Toda Ocasião'
  });

  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) navigate('/admin-painel-gestao');
  }, [navigate]);

  const getAuthHeaders = useCallback(() => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
  }), []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin-painel-gestao');
  };

  // Fetch functions
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/analytics?days=30`, getAuthHeaders());
      setAnalytics(response.data);
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/products`, getAuthHeaders());
      setProducts(response.data);
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  const fetchBrands = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/brands`, getAuthHeaders());
      setBrands(response.data);
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/categories`, getAuthHeaders());
      if (response.data && response.data.length > 0) {
        setCategories(response.data);
      }
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  const fetchBanners = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/banners`, getAuthHeaders());
      setBanners(response.data);
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  const fetchPopup = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/popup`, getAuthHeaders());
      setPopup(response.data);
      if (response.data) {
        setPopupForm({
          title: response.data.title || '',
          description: response.data.description || '',
          image: response.data.image || '',
          button_text: response.data.button_text || 'Ver Oferta',
          button_link: response.data.button_link || ''
        });
      }
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  const fetchSiteSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings`, getAuthHeaders());
      setSiteSettings(response.data);
      if (response.data) {
        setSettingsForm(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    checkAuth();
    fetchProducts();
    fetchBrands();
    fetchCategories();
  }, [checkAuth, fetchProducts, fetchBrands, fetchCategories]);

  useEffect(() => {
    if (activeTab === 'dashboard') fetchAnalytics();
    if (activeTab === 'products') fetchProducts();
    if (activeTab === 'brands') fetchBrands();
    if (activeTab === 'categories') fetchCategories();
    if (activeTab === 'banners') fetchBanners();
    if (activeTab === 'popup') fetchPopup();
    if (activeTab === 'settings') fetchSiteSettings();
  }, [activeTab, fetchAnalytics, fetchProducts, fetchBrands, fetchBanners, fetchPopup, fetchSiteSettings, fetchCategories]);

  // Image upload
  const handleImageUpload = async (e, type, variationIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Arquivo muito grande", description: "Máximo 5MB", variant: "destructive" });
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload-image`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = `${BACKEND_URL}${response.data.image_url}`;
      
      if (type === 'product') {
        setProductForm({ ...productForm, images: [...productForm.images, imageUrl] });
      } else if (type === 'variation' && variationIndex !== null) {
        const newVariations = [...productForm.variations];
        newVariations[variationIndex].image = imageUrl;
        setProductForm({ ...productForm, variations: newVariations });
      } else if (type === 'brand') {
        setBrandForm({ ...brandForm, logo: imageUrl });
      } else if (type === 'banner') {
        setBannerForm({ ...bannerForm, image: imageUrl });
      } else if (type === 'popup') {
        setPopupForm({ ...popupForm, image: imageUrl });
      }
      
      toast({ title: "✓ Imagem enviada!", description: "Upload realizado com sucesso." });
    } catch (error) {
      toast({ title: "Erro no upload", description: "Tente novamente", variant: "destructive" });
    }
    setLoading(false);
  };

  // Site Settings
  const saveSiteSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/settings`, settingsForm, getAuthHeaders());
      toast({ title: "✓ Configurações salvas!" });
      fetchSiteSettings();
    } catch (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
    setLoading(false);
  };

  // Variações
  const addVariation = () => {
    setProductForm({
      ...productForm,
      variations: [...productForm.variations, { name: '', image: '', description: '', size: '' }]
    });
  };

  const removeVariation = (index) => {
    const newVariations = productForm.variations.filter((_, i) => i !== index);
    setProductForm({ ...productForm, variations: newVariations });
  };

  const updateVariation = (index, field, value) => {
    const newVariations = [...productForm.variations];
    newVariations[index][field] = value;
    setProductForm({ ...productForm, variations: newVariations });
  };

  // CRUD Products
  const saveProduct = async (e) => {
    e.preventDefault();
    if (!productForm.name || !productForm.brand || !productForm.category) {
      toast({ title: "Preencha todos os campos obrigatórios", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const dataToSend = {
        ...productForm,
        variations: productForm.variations.filter(v => v.name.trim() !== '')
      };
      
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/admin/products/${editingItem.id}`, dataToSend, getAuthHeaders());
        toast({ title: "✓ Produto atualizado!" });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/products`, dataToSend, getAuthHeaders());
        toast({ title: "✓ Produto criado!" });
      }
      setShowProductForm(false);
      setEditingItem(null);
      setProductForm({ name: '', brand: '', category: '', description: '', images: [], is_promo: false, size: '', variations: [] });
      fetchProducts();
    } catch (error) {
      toast({ title: "Erro ao salvar", description: error.response?.data?.detail || "Tente novamente", variant: "destructive" });
    }
    setLoading(false);
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/products/${id}`, getAuthHeaders());
      toast({ title: "✓ Produto excluído!" });
      fetchProducts();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  // CRUD Brands
  const saveBrand = async (e) => {
    e.preventDefault();
    if (!brandForm.name || !brandForm.logo) {
      toast({ title: "Nome e logo são obrigatórios", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/admin/brands/${editingItem.id}`, brandForm, getAuthHeaders());
        toast({ title: "✓ Marca atualizada!" });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/brands`, brandForm, getAuthHeaders());
        toast({ title: "✓ Marca criada!" });
      }
      setShowBrandForm(false);
      setEditingItem(null);
      setBrandForm({ name: '', logo: '', description: '', featured: false });
      fetchBrands();
    } catch (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
    setLoading(false);
  };

  const deleteBrand = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta marca?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/brands/${id}`, getAuthHeaders());
      toast({ title: "✓ Marca excluída!" });
      fetchBrands();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  // CRUD Categories
  const saveCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name) {
      toast({ title: "Nome da categoria é obrigatório", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/admin/categories/${editingItem.id}`, categoryForm, getAuthHeaders());
        toast({ title: "✓ Categoria atualizada!" });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/categories`, categoryForm, getAuthHeaders());
        toast({ title: "✓ Categoria criada!" });
      }
      setShowCategoryForm(false);
      setEditingItem(null);
      setCategoryForm({ name: '', description: '', order: 0 });
      fetchCategories();
    } catch (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
    setLoading(false);
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/categories/${id}`, getAuthHeaders());
      toast({ title: "✓ Categoria excluída!" });
      fetchCategories();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  // CRUD Banners
  const saveBanner = async (e) => {
    e.preventDefault();
    if (!bannerForm.title || !bannerForm.image) {
      toast({ title: "Título e imagem são obrigatórios", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/admin/banners/${editingItem.id}`, bannerForm, getAuthHeaders());
        toast({ title: "✓ Banner atualizado!" });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/banners`, bannerForm, getAuthHeaders());
        toast({ title: "✓ Banner criado!" });
      }
      setShowBannerForm(false);
      setEditingItem(null);
      setBannerForm({ title: '', image: '', link: '', position: 'home_hero', order: 0 });
      fetchBanners();
    } catch (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
    setLoading(false);
  };

  const deleteBanner = async (id) => {
    if (!window.confirm('Excluir este banner?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/banners/${id}`, getAuthHeaders());
      toast({ title: "✓ Banner excluído!" });
      fetchBanners();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const toggleBanner = async (banner) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/banners/${banner.id}`, { is_active: !banner.is_active }, getAuthHeaders());
      toast({ title: banner.is_active ? "Banner desativado" : "Banner ativado" });
      fetchBanners();
    } catch (error) {
      toast({ title: "Erro ao atualizar", variant: "destructive" });
    }
  };

  // Popup
  const savePopup = async (e) => {
    e.preventDefault();
    if (!popupForm.title) {
      toast({ title: "Título é obrigatório", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      if (popup) {
        await axios.put(`${BACKEND_URL}/api/admin/popup`, popupForm, getAuthHeaders());
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/popup`, popupForm, getAuthHeaders());
      }
      toast({ title: "✓ Popup salvo!" });
      fetchPopup();
    } catch (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
    setLoading(false);
  };

  const togglePopup = async () => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/admin/popup/toggle`, {}, getAuthHeaders());
      toast({ title: response.data.is_active ? "✓ Popup ativado!" : "Popup desativado" });
      fetchPopup();
    } catch (error) {
      toast({ title: "Configure o popup primeiro", variant: "destructive" });
    }
  };

  // Menu items - REMOVIDO "Parceiro Destaque"
  const menuItems = [
    { id: 'dashboard', icon: BarChart3, label: 'Dashboard', desc: 'Visão geral' },
    { id: 'products', icon: Package, label: 'Produtos', desc: `${products.length} itens` },
    { id: 'categories', icon: LayoutDashboard, label: 'Categorias', desc: `${categories.length} categorias` },
    { id: 'brands', icon: Tags, label: 'Marcas', desc: `${brands.length} marcas` },
    { id: 'banners', icon: ImageIcon, label: 'Banners', desc: 'Publicidade' },
    { id: 'popup', icon: Bell, label: 'Popup', desc: popup?.is_active ? 'Ativo' : 'Inativo' },
    { id: 'settings', icon: Settings, label: 'Configurações', desc: 'Dados do site' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex">
      {/* Sidebar */}
      <div className="w-72 bg-gradient-to-b from-slate-800 to-slate-900 border-r border-slate-700/50 flex flex-col shadow-2xl">
        {/* Logo */}
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#EA1266] to-[#d00f58] rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <Sparkles size={20} className="text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base font-bold text-white truncate">Disneylândia</h1>
              <p className="text-slate-400 text-xs">Painel de Gestão</p>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                activeTab === item.id 
                  ? 'bg-gradient-to-r from-pink-500/20 to-pink-600/20 border border-pink-500/30 text-white shadow-lg' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className={`p-2 rounded-lg ${activeTab === item.id ? 'bg-pink-500/20' : 'bg-slate-700/50 group-hover:bg-slate-600/50'}`}>
                <item.icon size={18} className={activeTab === item.id ? 'text-pink-400' : ''} />
              </div>
              <div className="flex-1 text-left">
                <span className="font-medium block">{item.label}</span>
                <span className="text-xs text-slate-500">{item.desc}</span>
              </div>
              {activeTab === item.id && <ChevronRight size={16} className="text-pink-400" />}
            </button>
          ))}
        </nav>
        
        {/* User & Logout */}
        <div className="p-4 border-t border-slate-700/50">
          <div className="bg-slate-800/50 rounded-xl p-4 mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-600 to-pink-700 rounded-full flex items-center justify-center text-white font-bold">
                A
              </div>
              <div>
                <p className="text-white text-sm font-medium">Administrador</p>
                <p className="text-slate-400 text-xs">admin@disneylandia.com</p>
              </div>
            </div>
          </div>
          <Button onClick={handleLogout} variant="ghost" className="w-full justify-start text-slate-400 hover:text-pink-400 hover:bg-pink-500/10">
            <LogOut size={18} className="mr-3" /> Sair do Painel
          </Button>
          
          <div className="mt-4 pt-4 border-t border-slate-700/50">
            <p className="text-xs text-slate-500 text-center">
              Suporte técnico:{' '}
              <a href="https://instagram.com/gfcomunicacaoo" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300">
                @gfcomunicacaoo
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          {/* DASHBOARD */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Dashboard</h2>
                <p className="text-slate-400">Acompanhe o desempenho do seu site</p>
              </div>
              
              <Tip>
                Os dados são atualizados em tempo real. Clique em "Produtos" no menu para adicionar novos itens ao catálogo.
              </Tip>
              
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="bg-gradient-to-br from-pink-500 to-pink-600 border-0 shadow-xl shadow-pink-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-pink-100 text-sm font-medium">Visualizações</p>
                        <p className="text-4xl font-bold text-white mt-1">{analytics?.total_views || 0}</p>
                        <p className="text-pink-200 text-xs mt-2">últimos 30 dias</p>
                      </div>
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                        <Eye size={28} className="text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 border-0 shadow-xl shadow-emerald-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-emerald-100 text-sm font-medium">Contatos</p>
                        <p className="text-4xl font-bold text-white mt-1">{analytics?.total_contacts || 0}</p>
                        <p className="text-emerald-200 text-xs mt-2">cliques em WhatsApp/E-mail</p>
                      </div>
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                        <MousePointer size={28} className="text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 border-0 shadow-xl shadow-purple-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm font-medium">Orçamentos</p>
                        <p className="text-4xl font-bold text-white mt-1">{analytics?.total_quotes || 0}</p>
                        <p className="text-purple-200 text-xs mt-2">solicitações enviadas</p>
                      </div>
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                        <FileText size={28} className="text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-amber-500 to-amber-600 border-0 shadow-xl shadow-amber-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-100 text-sm font-medium">Produtos</p>
                        <p className="text-4xl font-bold text-white mt-1">{products.length}</p>
                        <p className="text-amber-200 text-xs mt-2">cadastrados no site</p>
                      </div>
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                        <Package size={28} className="text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts/Lists */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <TrendingUp className="text-pink-400" size={20} />
                      <h3 className="text-lg font-bold text-white">Produtos Mais Vistos</h3>
                    </div>
                    {analytics?.top_products?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.top_products.slice(0, 5).map((p, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-xl">
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 bg-pink-500/20 text-pink-400 rounded-full flex items-center justify-center text-xs font-bold">
                                {i + 1}
                              </span>
                              <span className="text-white text-sm">{p.product_name}</span>
                            </div>
                            <span className="text-pink-400 text-sm font-medium">{p.views} views</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Sparkles className="mx-auto text-slate-600 mb-3" size={40} />
                        <p className="text-slate-400">Os dados aparecerão aqui conforme os visitantes navegam</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <MousePointer className="text-emerald-400" size={20} />
                      <h3 className="text-lg font-bold text-white">Contatos por Tipo</h3>
                    </div>
                    {analytics?.contacts_by_type && Object.keys(analytics.contacts_by_type).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(analytics.contacts_by_type).map(([type, count]) => {
                          const typeConfig = {
                            whatsapp: { label: 'WhatsApp', color: 'text-green-400', bg: 'bg-green-500/20' },
                            email: { label: 'E-mail', color: 'text-blue-400', bg: 'bg-blue-500/20' },
                            orcamento: { label: 'Orçamento', color: 'text-purple-400', bg: 'bg-purple-500/20' },
                          };
                          const config = typeConfig[type] || { label: type, color: 'text-slate-400', bg: 'bg-slate-500/20' };
                          return (
                            <div key={type} className={`flex items-center justify-between p-3 ${config.bg} rounded-xl`}>
                              <span className={`${config.color} text-sm font-medium`}>{config.label}</span>
                              <span className={`${config.color} text-sm font-bold`}>{count} cliques</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Sparkles className="mx-auto text-slate-600 mb-3" size={40} />
                        <p className="text-slate-400">Aguardando interações dos visitantes</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* PRODUCTS */}
          {activeTab === 'products' && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">Produtos</h2>
                  <p className="text-slate-400">Gerencie o catálogo de produtos do site</p>
                </div>
                <Button onClick={() => { setShowProductForm(true); setEditingItem(null); setProductForm({ name: '', brand: '', category: '', description: '', images: [], is_promo: false, size: '', variations: [] }); }}
                  className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 shadow-lg">
                  <Plus size={18} className="mr-2" /> Novo Produto
                </Button>
              </div>

              <Tip>
                Para adicionar um produto: clique em "Novo Produto", preencha os dados e faça upload de imagens. 
                Você pode adicionar <strong>variações</strong> (cores, tamanhos) dentro de cada produto.
              </Tip>

              {showProductForm && (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">{editingItem ? '✏️ Editar Produto' : '➕ Novo Produto'}</h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowProductForm(false)} className="text-slate-400 hover:text-white">
                        <X size={20} />
                      </Button>
                    </div>
                    <form onSubmit={saveProduct} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Nome do Produto *</label>
                          <Input 
                            value={productForm.name} 
                            onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} 
                            placeholder="Ex: Coleção de Esmaltes Nude"
                            className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                            required 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Marca *</label>
                          <select 
                            value={productForm.brand} 
                            onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })}
                            className="w-full h-10 px-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white"
                            required
                          >
                            <option value="">Selecione a marca</option>
                            {brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
                          </select>
                          {brands.length === 0 && <p className="text-amber-400 text-xs mt-1">⚠️ Cadastre marcas primeiro</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Categoria *</label>
                          <select 
                            value={productForm.category} 
                            onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                            className="w-full h-10 px-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white"
                            required
                          >
                            <option value="">Selecione a categoria</option>
                            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Tamanho/Volume</label>
                          <Input 
                            value={productForm.size} 
                            onChange={(e) => setProductForm({ ...productForm, size: e.target.value })} 
                            placeholder="Ex: 100ml, 50g, Kit 3 peças"
                            className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Imagem Principal do Produto</label>
                        <Input 
                          type="file" 
                          accept="image/png,image/jpeg,image/webp" 
                          onChange={(e) => handleImageUpload(e, 'product')} 
                          className="bg-slate-700/50 border-slate-600 text-white file:bg-slate-600 file:text-white file:border-0"
                        />
                        <ImageSpec type="product" />
                      </div>
                      
                      {productForm.images.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Imagens adicionadas</label>
                          <div className="flex gap-3 flex-wrap">
                            {productForm.images.map((img, i) => (
                              <div key={i} className="relative group">
                                <img src={img} alt="" className="w-24 h-24 object-cover rounded-xl border-2 border-slate-600" />
                                <button 
                                  type="button" 
                                  onClick={() => setProductForm({ ...productForm, images: productForm.images.filter((_, idx) => idx !== i) })}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Descrição</label>
                        <Textarea 
                          value={productForm.description} 
                          onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} 
                          rows={3} 
                          placeholder="Descreva as características do produto..."
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                        />
                      </div>
                      
                      {/* Toggle Promoção */}
                      <div className="flex items-center gap-3 bg-slate-700/30 rounded-xl p-4">
                        <Switch 
                          checked={productForm.is_promo} 
                          onCheckedChange={(checked) => setProductForm({ ...productForm, is_promo: checked })}
                        />
                        <div>
                          <label className="text-sm font-medium text-slate-300">Produto em Promoção</label>
                          <p className="text-xs text-slate-500">Exibir na seção de "Promoções em Destaque" da página inicial</p>
                        </div>
                      </div>
                      
                      {/* VARIAÇÕES */}
                      <div className="border-t border-slate-700/50 pt-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <Layers size={18} className="text-pink-400" />
                            <h4 className="text-lg font-semibold text-white">Variações do Produto</h4>
                          </div>
                          <Button type="button" variant="outline" size="sm" onClick={addVariation} className="border-pink-500/50 text-pink-400 hover:bg-pink-500/10">
                            <Plus size={14} className="mr-1" /> Adicionar Variação
                          </Button>
                        </div>
                        <p className="text-slate-400 text-sm mb-4">
                          Adicione variações como cores, tamanhos ou opções diferentes do mesmo produto.
                        </p>
                        
                        {productForm.variations.length > 0 ? (
                          <div className="space-y-4">
                            {productForm.variations.map((variation, index) => (
                              <div key={index} className="bg-slate-700/30 rounded-xl p-4 relative">
                                <button 
                                  type="button" 
                                  onClick={() => removeVariation(index)}
                                  className="absolute top-2 right-2 text-red-400 hover:text-red-300"
                                >
                                  <X size={18} />
                                </button>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1">Nome da Variação *</label>
                                    <Input 
                                      value={variation.name}
                                      onChange={(e) => updateVariation(index, 'name', e.target.value)}
                                      placeholder="Ex: Vermelho Cereja"
                                      className="bg-slate-600/50 border-slate-500 text-white text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1">Tamanho (opcional)</label>
                                    <Input 
                                      value={variation.size || ''}
                                      onChange={(e) => updateVariation(index, 'size', e.target.value)}
                                      placeholder="Ex: 10ml"
                                      className="bg-slate-600/50 border-slate-500 text-white text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1">Imagem</label>
                                    <Input 
                                      type="file" 
                                      accept="image/png,image/jpeg,image/webp" 
                                      onChange={(e) => handleImageUpload(e, 'variation', index)}
                                      className="bg-slate-600/50 border-slate-500 text-white text-sm file:bg-slate-500 file:text-white file:border-0 file:text-xs"
                                    />
                                  </div>
                                </div>
                                <div className="mt-3">
                                  <label className="block text-xs font-medium text-slate-400 mb-1">Descrição (opcional)</label>
                                  <Input 
                                    value={variation.description || ''}
                                    onChange={(e) => updateVariation(index, 'description', e.target.value)}
                                    placeholder="Breve descrição da variação..."
                                    className="bg-slate-600/50 border-slate-500 text-white text-sm"
                                  />
                                </div>
                                {variation.image && (
                                  <div className="mt-3">
                                    <img src={variation.image} alt={variation.name} className="w-16 h-16 object-cover rounded-lg border border-slate-500" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-6 bg-slate-700/20 rounded-xl border border-dashed border-slate-600">
                            <Layers size={32} className="mx-auto text-slate-600 mb-2" />
                            <p className="text-slate-500 text-sm">Nenhuma variação adicionada</p>
                            <p className="text-slate-600 text-xs">Clique no botão acima para adicionar</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-3 pt-4 border-t border-slate-700/50">
                        <Button type="submit" disabled={loading} className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700">
                          <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Produto'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowProductForm(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                          Cancelar
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {products.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <Card key={product.id} className="bg-slate-800/50 border-slate-700/50 overflow-hidden group hover:border-slate-600 transition-all">
                      <div className="h-40 bg-slate-700/50 relative overflow-hidden">
                        {product.images?.[0] ? (
                          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon size={40} className="text-slate-600" />
                          </div>
                        )}
                        <div className="absolute top-2 right-2 flex gap-1">
                          {product.is_promo && (
                            <Badge className="bg-amber-500/90 text-white text-xs">Promo</Badge>
                          )}
                          <StatusBadge active={product.is_active} />
                        </div>
                        {product.variations?.length > 0 && (
                          <div className="absolute bottom-2 left-2">
                            <Badge className="bg-pink-500/90 text-white text-xs">
                              <Layers size={10} className="mr-1" /> {product.variations.length} variações
                            </Badge>
                          </div>
                        )}
                      </div>
                      <CardContent className="p-4">
                        <h4 className="font-bold text-white truncate">{product.name}</h4>
                        <p className="text-slate-400 text-sm">{product.brand} • {categories.find(c => c.id === product.category)?.name || product.category}</p>
                        {product.size && <p className="text-slate-500 text-xs mt-1">Tamanho: {product.size}</p>}
                        <div className="flex gap-2 mt-4">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                            onClick={() => { 
                              setEditingItem(product); 
                              setProductForm({ 
                                name: product.name, 
                                brand: product.brand, 
                                category: product.category, 
                                description: product.description || '', 
                                images: product.images || [],
                                is_promo: product.is_promo || false,
                                size: product.size || '',
                                variations: product.variations || []
                              }); 
                              setShowProductForm(true); 
                            }}
                          >
                            <Edit size={14} className="mr-1" /> Editar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            onClick={() => deleteProduct(product.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-12 text-center">
                    <Package className="mx-auto text-slate-600 mb-4" size={48} />
                    <h3 className="text-xl font-bold text-white mb-2">Nenhum produto cadastrado</h3>
                    <p className="text-slate-400 mb-6">Comece adicionando seu primeiro produto ao catálogo</p>
                    <Button onClick={() => setShowProductForm(true)} className="bg-gradient-to-r from-pink-500 to-pink-600">
                      <Plus size={18} className="mr-2" /> Adicionar Primeiro Produto
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* CATEGORIES */}
          {activeTab === 'categories' && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">Categorias</h2>
                  <p className="text-slate-400">Gerencie as categorias de produtos</p>
                </div>
                <Button onClick={() => { setShowCategoryForm(true); setEditingItem(null); setCategoryForm({ name: '', description: '', order: 0 }); }}
                  className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 shadow-lg">
                  <Plus size={18} className="mr-2" /> Nova Categoria
                </Button>
              </div>

              <Tip>
                As categorias organizam os produtos no catálogo. Use a ordem para definir a posição no filtro (menor número = aparece primeiro).
              </Tip>

              {showCategoryForm && (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">{editingItem ? '✏️ Editar Categoria' : '➕ Nova Categoria'}</h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowCategoryForm(false)} className="text-slate-400 hover:text-white">
                        <X size={20} />
                      </Button>
                    </div>
                    <form onSubmit={saveCategory} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Nome da Categoria *</label>
                          <Input 
                            value={categoryForm.name} 
                            onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} 
                            placeholder="Ex: Maquiagem"
                            className="bg-slate-700/50 border-slate-600 text-white"
                            required 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Ordem de Exibição</label>
                          <Input 
                            type="number"
                            value={categoryForm.order} 
                            onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })} 
                            placeholder="0"
                            className="bg-slate-700/50 border-slate-600 text-white"
                          />
                          <p className="text-slate-500 text-xs mt-1">Menor número = aparece primeiro</p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Descrição (opcional)</label>
                        <Textarea 
                          value={categoryForm.description} 
                          onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} 
                          rows={2} 
                          placeholder="Uma breve descrição da categoria..."
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button type="submit" disabled={loading} className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700">
                          <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Categoria'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowCategoryForm(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                          Cancelar
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {categories.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map((category) => (
                    <Card key={category.id} className="bg-slate-800/50 border-slate-700/50 hover:border-slate-600 transition-all">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center">
                                <LayoutDashboard size={20} className="text-white" />
                              </div>
                              <div>
                                <h4 className="font-bold text-white">{category.name}</h4>
                                <p className="text-slate-500 text-xs">Ordem: {category.order || 0}</p>
                              </div>
                            </div>
                            {category.description && (
                              <p className="text-slate-400 text-sm mt-2 line-clamp-2">{category.description}</p>
                            )}
                          </div>
                          <StatusBadge active={category.is_active !== false} />
                        </div>
                        <div className="flex gap-2 mt-4 pt-4 border-t border-slate-700/50">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                            onClick={() => { 
                              setEditingItem(category); 
                              setCategoryForm({ name: category.name, description: category.description || '', order: category.order || 0 }); 
                              setShowCategoryForm(true); 
                            }}
                          >
                            <Edit size={14} className="mr-1" /> Editar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            onClick={() => deleteCategory(category.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-slate-800/50 border-slate-700/50 border-dashed">
                  <CardContent className="p-12 text-center">
                    <LayoutDashboard size={48} className="mx-auto text-slate-600 mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">Nenhuma categoria cadastrada</h3>
                    <p className="text-slate-400 mb-6">Crie categorias para organizar seus produtos no catálogo.</p>
                    <Button onClick={() => setShowCategoryForm(true)} className="bg-gradient-to-r from-pink-500 to-pink-600">
                      <Plus size={18} className="mr-2" /> Criar Primeira Categoria
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* BRANDS */}
          {activeTab === 'brands' && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">Marcas</h2>
                  <p className="text-slate-400">Gerencie as marcas disponíveis no site</p>
                </div>
                <Button onClick={() => { setShowBrandForm(true); setEditingItem(null); setBrandForm({ name: '', logo: '', description: '', featured: false }); }}
                  className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 shadow-lg">
                  <Plus size={18} className="mr-2" /> Nova Marca
                </Button>
              </div>

              <Tip>
                As marcas marcadas como "Destaque" aparecem em tamanho maior na página inicial. 
                <strong className="text-pink-400"> Tamanho ideal da logo: 400 x 200 pixels</strong>. 
                Use PNG com fundo transparente ou SVG para melhor resultado.
              </Tip>

              {showBrandForm && (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">{editingItem ? '✏️ Editar Marca' : '➕ Nova Marca'}</h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowBrandForm(false)} className="text-slate-400 hover:text-white">
                        <X size={20} />
                      </Button>
                    </div>
                    <form onSubmit={saveBrand} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Nome da Marca *</label>
                          <Input 
                            value={brandForm.name} 
                            onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })} 
                            placeholder="Ex: O Boticário"
                            className="bg-slate-700/50 border-slate-600 text-white"
                            required 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Logo da Marca *</label>
                          <Input 
                            type="file" 
                            accept="image/png,image/svg+xml,image/jpeg" 
                            onChange={(e) => handleImageUpload(e, 'brand')} 
                            className="bg-slate-700/50 border-slate-600 text-white file:bg-slate-600 file:text-white file:border-0"
                          />
                          <ImageSpec type="brand" />
                          {!brandForm.logo && (
                            <div className="mt-2">
                              <Input 
                                placeholder="Ou cole a URL da logo"
                                value={brandForm.logo}
                                onChange={(e) => setBrandForm({ ...brandForm, logo: e.target.value })}
                                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      {brandForm.logo && (
                        <div className="flex items-center gap-4 p-4 bg-slate-700/30 rounded-xl">
                          <img src={brandForm.logo} alt="Preview" className="h-16 w-auto object-contain bg-white p-2 rounded-lg" />
                          <Button type="button" variant="outline" size="sm" onClick={() => setBrandForm({ ...brandForm, logo: '' })} className="border-slate-600 text-slate-300">
                            Remover
                          </Button>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Descrição (opcional)</label>
                        <Textarea 
                          value={brandForm.description} 
                          onChange={(e) => setBrandForm({ ...brandForm, description: e.target.value })} 
                          rows={2}
                          placeholder="Breve descrição da marca..."
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                        />
                      </div>
                      <div className="flex items-center gap-3 p-4 bg-slate-700/30 rounded-xl">
                        <Switch checked={brandForm.featured} onCheckedChange={(v) => setBrandForm({ ...brandForm, featured: v })} />
                        <div>
                          <label className="text-white font-medium">Marca em Destaque</label>
                          <p className="text-slate-400 text-sm">Aparece maior na página inicial</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <Button type="submit" disabled={loading} className="bg-gradient-to-r from-pink-500 to-pink-600">
                          <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Marca'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowBrandForm(false)} className="border-slate-600 text-slate-300">
                          Cancelar
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {brands.map((brand) => (
                  <Card key={brand.id} className={`bg-slate-800/50 border-slate-700/50 overflow-hidden group hover:border-slate-600 transition-all ${brand.featured ? 'ring-2 ring-pink-500/50' : ''}`}>
                    <div className="h-24 bg-white p-4 flex items-center justify-center">
                      {brand.logo ? (
                        <img src={brand.logo} alt={brand.name} className="max-h-full max-w-full object-contain" />
                      ) : (
                        <Tags size={32} className="text-slate-300" />
                      )}
                    </div>
                    <CardContent className="p-3">
                      <h4 className="font-bold text-white text-sm truncate">{brand.name}</h4>
                      {brand.featured && <span className="text-pink-400 text-xs">★ Destaque</span>}
                      <div className="flex gap-1 mt-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
                          onClick={() => { 
                            setEditingItem(brand); 
                            setBrandForm({ 
                              name: brand.name, 
                              logo: brand.logo, 
                              description: brand.description || '', 
                              featured: brand.featured 
                            }); 
                            setShowBrandForm(true); 
                          }}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => deleteBrand(brand.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {brands.length === 0 && (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-12 text-center">
                    <Tags className="mx-auto text-slate-600 mb-4" size={48} />
                    <p className="text-slate-400">Nenhuma marca cadastrada ainda</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* BANNERS */}
          {activeTab === 'banners' && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">Banners</h2>
                  <p className="text-slate-400">Gerencie os banners de publicidade do site</p>
                </div>
                <Button onClick={() => { setShowBannerForm(true); setEditingItem(null); setBannerForm({ title: '', image: '', link: '', position: 'home_hero', order: 0 }); }}
                  className="bg-gradient-to-r from-pink-500 to-pink-600 shadow-lg">
                  <Plus size={18} className="mr-2" /> Novo Banner
                </Button>
              </div>

              <Tip>
                Use banners para destacar promoções, parcerias ou ofertas especiais. 
                <strong className="text-pink-400"> Cada posição tem um tamanho específico</strong> - veja as medidas ao selecionar a posição.
              </Tip>

              {showBannerForm && (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">{editingItem ? '✏️ Editar Banner' : '➕ Novo Banner'}</h3>
                      <Button variant="ghost" size="sm" onClick={() => setShowBannerForm(false)} className="text-slate-400"><X size={20} /></Button>
                    </div>
                    <form onSubmit={saveBanner} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Título *</label>
                          <Input value={bannerForm.title} onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })} className="bg-slate-700/50 border-slate-600 text-white" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Posição *</label>
                          <select value={bannerForm.position} onChange={(e) => setBannerForm({ ...bannerForm, position: e.target.value })} className="w-full h-10 px-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white" required>
                            {bannerPositions.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.size})</option>)}
                          </select>
                          <p className="text-slate-500 text-xs mt-1">{bannerPositions.find(p => p.id === bannerForm.position)?.desc}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Imagem *</label>
                          <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => handleImageUpload(e, 'banner')} className="bg-slate-700/50 border-slate-600 text-white file:bg-slate-600 file:text-white file:border-0" />
                          <ImageSpec type="banner" position={bannerForm.position} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Link (opcional)</label>
                          <Input value={bannerForm.link} onChange={(e) => setBannerForm({ ...bannerForm, link: e.target.value })} placeholder="/catalogo ou URL externa" className="bg-slate-700/50 border-slate-600 text-white" />
                        </div>
                      </div>
                      {bannerForm.image && (
                        <div className="p-4 bg-slate-700/30 rounded-xl">
                          <p className="text-slate-400 text-sm mb-2">Preview:</p>
                          <img src={bannerForm.image} alt="Preview" className="max-h-40 rounded-lg" />
                        </div>
                      )}
                      <div className="flex gap-3">
                        <Button type="submit" disabled={loading} className="bg-gradient-to-r from-pink-500 to-pink-600">
                          <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Banner'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowBannerForm(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {banners.map((banner) => (
                  <Card key={banner.id} className="bg-slate-800/50 border-slate-700/50 overflow-hidden">
                    <div className="h-32 bg-slate-700/50 relative">
                      {banner.image ? (
                        <img src={banner.image} alt={banner.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><ImageIcon size={40} className="text-slate-600" /></div>
                      )}
                      <div className="absolute top-2 right-2"><StatusBadge active={banner.is_active} /></div>
                    </div>
                    <CardContent className="p-4">
                      <h4 className="font-bold text-white">{banner.title}</h4>
                      <p className="text-slate-400 text-sm">{bannerPositions.find(p => p.id === banner.position)?.name || banner.position}</p>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" className="flex-1 border-slate-600 text-slate-300" onClick={() => toggleBanner(banner)}>
                          {banner.is_active ? 'Desativar' : 'Ativar'}
                        </Button>
                        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => { setEditingItem(banner); setBannerForm(banner); setShowBannerForm(true); }}>
                          <Edit size={14} />
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-500/50 text-red-400" onClick={() => deleteBanner(banner.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {banners.length === 0 && (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-12 text-center">
                    <ImageIcon className="mx-auto text-slate-600 mb-4" size={48} />
                    <p className="text-slate-400">Nenhum banner cadastrado</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* POPUP */}
          {activeTab === 'popup' && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">Popup Promocional</h2>
                  <p className="text-slate-400">Configure o popup que aparece para os visitantes</p>
                </div>
                <Button onClick={togglePopup} variant={popup?.is_active ? "destructive" : "default"} className={popup?.is_active ? "" : "bg-gradient-to-r from-green-500 to-green-600"}>
                  {popup?.is_active ? 'Desativar Popup' : 'Ativar Popup'}
                </Button>
              </div>

              <Tip>
                O popup aparece uma vez por sessão para cada visitante. Use para destacar promoções especiais ou novidades.
              </Tip>

              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardContent className="p-6">
                  <form onSubmit={savePopup} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Título *</label>
                        <Input value={popupForm.title} onChange={(e) => setPopupForm({ ...popupForm, title: e.target.value })} placeholder="Ex: Super Promoção!" className="bg-slate-700/50 border-slate-600 text-white" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Imagem</label>
                        <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'popup')} className="bg-slate-700/50 border-slate-600 text-white file:bg-slate-600 file:text-white file:border-0" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Descrição</label>
                      <Textarea value={popupForm.description} onChange={(e) => setPopupForm({ ...popupForm, description: e.target.value })} rows={3} placeholder="Descrição da promoção..." className="bg-slate-700/50 border-slate-600 text-white" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Texto do Botão</label>
                        <Input value={popupForm.button_text} onChange={(e) => setPopupForm({ ...popupForm, button_text: e.target.value })} placeholder="Ver Oferta" className="bg-slate-700/50 border-slate-600 text-white" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Link do Botão</label>
                        <Input value={popupForm.button_link} onChange={(e) => setPopupForm({ ...popupForm, button_link: e.target.value })} placeholder="/catalogo" className="bg-slate-700/50 border-slate-600 text-white" />
                      </div>
                    </div>
                    <Button type="submit" disabled={loading} className="bg-gradient-to-r from-pink-500 to-pink-600">
                      <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Popup'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* SETTINGS - Configurações Gerais */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Configurações do Site</h2>
                <p className="text-slate-400">Edite as informações de contato e configurações gerais</p>
              </div>

              <Tip>
                Estas informações são exibidas no site e usadas para contato com clientes. 
                Mantenha sempre atualizadas.
              </Tip>

              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardContent className="p-6">
                  <form onSubmit={saveSiteSettings} className="space-y-6">
                    {/* Informações de Contato */}
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Settings size={18} className="text-pink-400" />
                        Informações de Contato
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">E-mail do Site</label>
                          <Input 
                            value={settingsForm.site_email} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, site_email: e.target.value })} 
                            placeholder="contato@seusite.com"
                            className="bg-slate-700/50 border-slate-600 text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Telefone</label>
                          <Input 
                            value={settingsForm.site_phone} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, site_phone: e.target.value })} 
                            placeholder="(00) 00000-0000"
                            className="bg-slate-700/50 border-slate-600 text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">WhatsApp (só números)</label>
                          <Input 
                            value={settingsForm.site_whatsapp} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, site_whatsapp: e.target.value })} 
                            placeholder="5594991666990"
                            className="bg-slate-700/50 border-slate-600 text-white"
                          />
                          <p className="text-slate-500 text-xs mt-1">Formato: código do país + DDD + número (sem espaços)</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Instagram</label>
                          <Input 
                            value={settingsForm.site_instagram} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, site_instagram: e.target.value })} 
                            placeholder="@seuinstagram"
                            className="bg-slate-700/50 border-slate-600 text-white"
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-slate-300 mb-2">Endereço Completo</label>
                        <Input 
                          value={settingsForm.site_address} 
                          onChange={(e) => setSettingsForm({ ...settingsForm, site_address: e.target.value })} 
                          placeholder="Rua, número - Cidade, UF"
                          className="bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                    </div>

                    {/* Indicadores da Home */}
                    <div className="border-t border-slate-700/50 pt-6">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Sparkles size={18} className="text-pink-400" />
                        Indicadores da Página Inicial
                      </h3>
                      <p className="text-slate-400 text-sm mb-4">
                        Estes indicadores aparecem no topo da página inicial, abaixo do banner.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-slate-700/30 rounded-xl p-4">
                          <label className="block text-xs font-medium text-slate-400 mb-2">Indicador 1 - Título</label>
                          <Input 
                            value={settingsForm.hero_indicator1_title} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, hero_indicator1_title: e.target.value })} 
                            className="bg-slate-600/50 border-slate-500 text-white text-sm mb-2"
                          />
                          <label className="block text-xs font-medium text-slate-400 mb-2">Subtítulo</label>
                          <Input 
                            value={settingsForm.hero_indicator1_subtitle} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, hero_indicator1_subtitle: e.target.value })} 
                            className="bg-slate-600/50 border-slate-500 text-white text-sm"
                          />
                        </div>
                        <div className="bg-slate-700/30 rounded-xl p-4">
                          <label className="block text-xs font-medium text-slate-400 mb-2">Indicador 2 - Título</label>
                          <Input 
                            value={settingsForm.hero_indicator2_title} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, hero_indicator2_title: e.target.value })} 
                            className="bg-slate-600/50 border-slate-500 text-white text-sm mb-2"
                          />
                          <label className="block text-xs font-medium text-slate-400 mb-2">Subtítulo</label>
                          <Input 
                            value={settingsForm.hero_indicator2_subtitle} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, hero_indicator2_subtitle: e.target.value })} 
                            className="bg-slate-600/50 border-slate-500 text-white text-sm"
                          />
                        </div>
                        <div className="bg-slate-700/30 rounded-xl p-4">
                          <label className="block text-xs font-medium text-slate-400 mb-2">Indicador 3 - Título</label>
                          <Input 
                            value={settingsForm.hero_indicator3_title} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, hero_indicator3_title: e.target.value })} 
                            className="bg-slate-600/50 border-slate-500 text-white text-sm mb-2"
                          />
                          <label className="block text-xs font-medium text-slate-400 mb-2">Subtítulo</label>
                          <Input 
                            value={settingsForm.hero_indicator3_subtitle} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, hero_indicator3_subtitle: e.target.value })} 
                            className="bg-slate-600/50 border-slate-500 text-white text-sm"
                          />
                        </div>
                        <div className="bg-slate-700/30 rounded-xl p-4">
                          <label className="block text-xs font-medium text-slate-400 mb-2">Indicador 4 - Título</label>
                          <Input 
                            value={settingsForm.hero_indicator4_title} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, hero_indicator4_title: e.target.value })} 
                            className="bg-slate-600/50 border-slate-500 text-white text-sm mb-2"
                          />
                          <label className="block text-xs font-medium text-slate-400 mb-2">Subtítulo</label>
                          <Input 
                            value={settingsForm.hero_indicator4_subtitle} 
                            onChange={(e) => setSettingsForm({ ...settingsForm, hero_indicator4_subtitle: e.target.value })} 
                            className="bg-slate-600/50 border-slate-500 text-white text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-700/50">
                      <Button type="submit" disabled={loading} className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700">
                        <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Configurações'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
